<script setup>
import { ref, computed, onMounted } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { infoBoxIdEnum, infoBoxToneEnum } from '@/stores/portal.store'

import { useChips } from '@/composables/chips'
import { useDefaultGridOptions } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { expenseClaimReport, expenseRowAmount, formatExpenseMoney, groupExpensesByDate, sumExpenseAmounts, useExpenseClaimActions } from '@/composables/expense-claim'

import ActionsMenuButton from '@/components/shared/ActionsMenuButton.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import GridDetailInfo from '@/components/shared/GridDetailInfo.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ExpenseDayHeader from '@/components/accounting/ExpenseDayHeader.vue'
import ExpenseRowDetail from '@/components/accounting/ExpenseRowDetail.vue'
import ExpenseRowListItem from '@/components/accounting/ExpenseRowListItem.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const { isMobile } = useDetectDevice()
const { getExpenseClaimStatusChips, getProjectsSummaryChips } = useChips()
const { canApprove, canRevertApproval, canPay, canDelete, approveAsync, rejectAsync, revertApprovalAsync, payAsync, promptAsync } = useExpenseClaimActions()

const claim = ref({ ...props.modelValue })
const rows = ref([])
const exclusions = ref({})
const changed = ref(false)
const showPdf = ref(false)
const showDeleteConfirm = ref(false)
const detailIndex = ref(null)

useNavigator(() => close())

const close = () => emit(changed.value ? 'update' : 'cancel')
const isApproving = computed(() => canApprove(claim.value))
const isExcluding = (row) => exclusions.value[row.id] !== undefined
const isIncluded = (row) => !row.is_excluded && !isExcluding(row)
const exclusionReason = (row) => row.exclusion_reason || exclusions.value[row.id]
const approvedTotal = computed(() => sumExpenseAmounts(rows.value.filter(isIncluded)))
const days = computed(() => groupExpensesByDate(rows.value))
const orderedRows = computed(() => days.value.flatMap(day => day.rows))
const openRow = (row) => { detailIndex.value = orderedRows.value.findIndex(item => item.id === row.id) }


const signedStamp = (label, date) => (date ? [label, formatShortDate(date)].filter(Boolean).join(' · ') : null)
const infoItems = computed(() => [
  { label: i18n.t('common.staff'), value: claim.value.staff_label },
  { label: i18n.t('common.period'), value: claim.value.date_from ? `${formatShortDate(claim.value.date_from)} – ${formatShortDate(claim.value.date_to)}` : null },
  { label: i18n.t('common.expenseClaimTotal'), value: formatExpenseMoney(isApproving.value ? approvedTotal.value : claim.value.total_reference) },
  { label: i18n.t('common.createdBy'), value: signedStamp(claim.value.created_by_label, claim.value.created_at) },
  { label: i18n.t('common.expenseClaimApprovedBy'), value: signedStamp(claim.value.approved_by_label, claim.value.approved_at) },
  { label: i18n.t('common.updatedBy'), value: signedStamp(claim.value.updated_by_label, claim.value.updated_at) },
].filter(item => item.value))

const loadAsync = async() => {
  rows.value = await accountingStore.fetchExpenseClaimRowsAsync(claim.value.id)
}

const refreshAsync = async(updated = null) => {
  changed.value = true
  claim.value = updated ?? await accountingStore.fetchExpenseClaimByIdAsync(claim.value.id)
  exclusions.value = {}
  await loadAsync()
}

const onToggleExclusion = async(row) => {
  if (isExcluding(row)) {
    const rest = { ...exclusions.value }
    delete rest[row.id]
    exclusions.value = rest
    return
  }
  const reason = await promptAsync({ title: i18n.t('common.expenseClaimExcludeRow'), icon: 'mdi-close-circle-outline', iconColor: 'warning', inputLabel: i18n.t('common.expenseClaimRejectionReason'), okColor: 'warning' })
  if (reason) exclusions.value = { ...exclusions.value, [row.id]: reason }
}

const onApproveAsync = async() => {
  const [updated] = await approveAsync([claim.value], { [claim.value.id]: exclusions.value })
  await refreshAsync(updated)
}
const onRejectAsync = async() => {
  const updated = await rejectAsync(claim.value)
  if (updated) await refreshAsync(updated[0])
}
const onRevertApprovalAsync = async() => {
  const [updated] = await revertApprovalAsync([claim.value])
  await refreshAsync(updated)
}
const onPayAsync = async() => {
  const updated = await payAsync([claim.value])
  if (updated) await refreshAsync(updated[0])
}
const onDeleteConfirmedAsync = async() => {
  await accountingStore.deleteExpenseClaimAsync(claim.value.id)
  emit('update')
}

const claimActions = computed(() => [
  { key: 'delete', label: i18n.t('common.delete'), icon: 'mdi-delete-outline', visible: canDelete(claim.value), destructive: true, handler: () => { showDeleteConfirm.value = true } },
  { key: 'restore', label: i18n.t('common.restore'), icon: 'mdi-undo-variant', visible: canRevertApproval(claim.value), handler: onRevertApprovalAsync },
  { key: 'reject', label: i18n.t('common.reject'), icon: 'mdi-close-circle-outline', visible: isApproving.value, handler: onRejectAsync },
  { key: 'approve', label: i18n.t('common.approve'), icon: 'mdi-check', visible: isApproving.value, primary: true, handler: onApproveAsync },
  { key: 'pay', label: i18n.t('common.expenseClaimMarkPaid'), icon: 'mdi-cash-check', visible: canPay(claim.value), primary: true, handler: onPayAsync },
].filter(action => action.visible))

const menuActions = computed(() => [
  { key: 'print', label: i18n.t('common.print'), icon: 'mdi-printer', handler: () => { showPdf.value = true } },
  ...claimActions.value,
])

const hasBottomBar = computed(() => !isMobile.value && claimActions.value.length > 0)

const gridOptions = useDefaultGridOptions({
  columns: [
    {
      type: 'buttons',
      buttons: [
        { hint: i18n.t('common.expenseClaimExcludeRow'), icon: 'mdi mdi-close-circle-outline', visible: (e) => isApproving.value && !isExcluding(e.row.data), onClick: (e) => onToggleExclusion(e.row.data) },
        { hint: i18n.t('common.restore'), icon: 'mdi mdi-undo', visible: (e) => isApproving.value && isExcluding(e.row.data), onClick: (e) => onToggleExclusion(e.row.data) },
      ],
      fixed: false,
      visibleIndex: -1,
      showInColumnChooser: false,
      width: '3rem',
      visible: isApproving.value,
    },
    dataGridColumn.useDateColumn({ width: '7rem' }),
    { dataField: 'tag_id', caption: i18n.t('common.expense'), calculateDisplayValue: row => accountingStore.costReportRowTagById.get(row.tag_id)?.name },
    { dataField: 'project_name', caption: i18n.t('common.project'), calculateDisplayValue: row => row.project_name ?? i18n.t('common.generalExpenses') },
    dataGridColumn.useNotesColumn(),
    { dataField: 'amount', caption: i18n.t('common.amount'), dataType: 'number', calculateDisplayValue: row => formatExpenseMoney(row.amount ?? expenseRowAmount(row)), width: '8rem' },
    { dataField: 'files', caption: i18n.t('common.attachment', 2), cellTemplate: 'files-cell', allowFiltering: false, allowSorting: false, width: '6rem' },
    { caption: i18n.t('common.expenseClaimExcludeRow'), calculateCellValue: exclusionReason, allowFiltering: false, allowSorting: false },
  ],
  keyExpr: 'id',
  isDetailGrid: true,
  height: 'auto',
  remoteOperations: false,
  paging: { enabled: false },
  filterRow: { visible: false },
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  onRowPrepared: (e) => {
    if (e.rowType === 'data' && !isIncluded(e.data)) e.rowElement.classList.add('text-strike', 'text-grey-6')
  },
  onRowDblClick: (e) => { if (e.rowType === 'data') openRow(e.data) },
})

onMounted(async() => {
  if (!accountingStore.costReportRowTags.length) await accountingStore.fetchCostReportRowTagsAsync({}, true)
  await loadAsync()
})
</script>

<template>
  <q-card class="dialog-card-flex">
    <confirm-dialog
      v-model="showDeleteConfirm"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmedAsync"
    />
    <q-dialog v-model="showPdf" :maximized="isMobile">
      <report-file-dialog
        v-if="showPdf"
        :report="expenseClaimReport(claim)"
        @cancel="showPdf = false"
      />
    </q-dialog>

    <q-dialog :model-value="detailIndex !== null" maximized @hide="detailIndex = null">
      <expense-row-detail
        v-if="detailIndex !== null"
        v-model:index="detailIndex"
        :rows="orderedRows"
        :exclusions="exclusions"
        :can-exclude="isApproving"
        @toggle-exclusion="onToggleExclusion"
        @cancel="detailIndex = null"
      />
    </q-dialog>

    <navigate-back :text="`${i18n.t('common.expenseClaim')} ${claim.number}`" @cancel="close">
      <template v-if="isMobile" #right>
        <actions-menu-button :actions="menuActions" />
      </template>
    </navigate-back>

    <q-card-section class="scroll col q-pt-none" :class="{ 'dialog-card-content': hasBottomBar }">
      <grid-detail-info :items="infoItems" class="q-pa-sm">
        <div>
          <div class="text-caption text-grey-7">{{ i18n.t('common.status') }}</div>
          <chip-template :values="getExpenseClaimStatusChips(claim)" />
        </div>
        <div v-if="claim.projects?.length" class="grid-detail-info__wide">
          <div class="text-caption text-grey-7">{{ i18n.t('common.project', 2) }}</div>
          <chip-template :values="getProjectsSummaryChips(claim.projects)" />
        </div>
      </grid-detail-info>
      <info-box
        v-if="claim.rejection_reason"
        class="q-my-sm"
        :tone="infoBoxToneEnum.WARNING"
        :message="i18n.t('common.expenseClaimRejectedBecause', { reason: claim.rejection_reason })"
      />
      <info-box
        v-if="isApproving"
        :id="infoBoxIdEnum.EXPENSE_CLAIM_APPROVAL"
        class="q-my-sm"
        :message="i18n.t('info.expenseClaimApprovalHint')"
      />

      <template v-if="isMobile">
        <div v-for="day in days" :key="day.date">
          <expense-day-header :date="day.date" :total="sumExpenseAmounts(day.rows.filter(isIncluded))" />
          <q-list>
            <q-item
              v-for="row in day.rows"
              :key="row.id"
              class="q-pa-none column"
              clickable
              @click="openRow(row)"
            >
              <expense-row-list-item
                :row="{ ...row, row_files: row.files }"
                class="full-width"
                :class="{ 'text-strike text-grey-6': !isIncluded(row) }"
              />
              <div v-if="exclusionReason(row)" class="text-caption text-warning q-px-md q-pb-xs">
                {{ i18n.t('common.expenseClaimExcludedBecause', { reason: exclusionReason(row) }) }}
              </div>
            </q-item>
          </q-list>
        </div>
      </template>
      <dx-data-grid
        v-else
        v-bind="gridOptions"
        class="q-mt-sm"
        :data-source="orderedRows"
      >
        <template #files-cell="{ data: templateOptions }">
          <q-icon
            v-if="templateOptions.data.files?.length"
            name="mdi-paperclip"
            color="grey-7"
            size="xs"
          />
        </template>
      </dx-data-grid>
    </q-card-section>

    <q-card-actions v-if="hasBottomBar" class="align-center dialog-card-bottom-bar q-gutter-sm">
      <q-btn
        v-for="action in claimActions"
        :key="action.key"
        :class="{ 'large-btn': action.primary }"
        :flat="!action.primary"
        :color="action.primary ? 'positive' : action.destructive ? 'negative' : undefined"
        :label="action.label"
        @click="action.handler()"
      />
    </q-card-actions>
  </q-card>
</template>
